import React, { useState, useEffect, useCallback } from 'react'
import {
  ShieldCheckIcon,
  PlusIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  TrashIcon,
  ChevronDownIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'

const LEVEL_BADGES = {
  mandatory: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', label: 'Mandatory' },
  recommended: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200', label: 'Recommended' },
  optional: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', label: 'Optional' },
}

const LEVEL_ICONS = {
  mandatory: ExclamationTriangleIcon,
  recommended: InformationCircleIcon,
  optional: CheckCircleIcon,
}

export default function ApprovalPolicies() {
  const [triggers, setTriggers] = useState([])
  const [approvers, setApprovers] = useState([])
  const [availableRoles, setAvailableRoles] = useState([])
  const [availableStaff, setAvailableStaff] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [showAddApproverForm, setShowAddApproverForm] = useState(false)
  const [newTrigger, setNewTrigger] = useState({
    trigger_code: '',
    name: '',
    description: '',
    regulatory_reference: 'Internal policy',
    requirement_level: 'recommended',
    enabled: true,
  })
  const [newApprover, setNewApprover] = useState({
    approver_type: 'role',
    approver_value: '',
    display_name: '',
  })

  const loadTriggers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const [triggersRes, approversRes, rolesRes, usersRes] = await Promise.all([
        authFetch(getLocalApiUrl('/api/ecdd-approval-triggers')),
        authFetch(getLocalApiUrl('/api/ecdd-approval-triggers/approvers')),
        authFetch(getLocalApiUrl('/api/admin/roles')),
        authFetch(getLocalApiUrl('/api/admin/users')),
      ])
      const triggersJson = await triggersRes.json()
      const approversJson = await approversRes.json()
      const rolesJson = await rolesRes.json()
      const usersJson = await usersRes.json()
      if (!triggersJson.success) throw new Error(triggersJson.error || 'Failed to load triggers')
      setTriggers(triggersJson.data || [])
      setApprovers(approversJson.data || [])
      // Map roles to { value, label } format
      const roles = (rolesJson.data || [])
        .map(r => ({ value: r.role, label: r.display_name || r.role }))
      setAvailableRoles(roles)
      // Map users to selectable format (only active users)
      const users = (usersJson.data || [])
        .filter(u => u.active !== false)
        .map(u => ({ value: u.username, label: u.name || u.username, role: u.role, email: u.email }))
      setAvailableStaff(users)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTriggers()
  }, [loadTriggers])

  const updateTrigger = async (triggerCode, updates) => {
    try {
      setSaving(triggerCode)
      const res = await authFetch(getLocalApiUrl(`/api/ecdd-approval-triggers/${triggerCode}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Update failed')
      setTriggers((prev) =>
        prev.map((t) => (t.trigger_code === triggerCode ? { ...t, ...json.data } : t))
      )
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(null)
    }
  }

  const createTrigger = async () => {
    if (!newTrigger.trigger_code || !newTrigger.name) return
    try {
      setSaving('new')
      const res = await authFetch(getLocalApiUrl('/api/ecdd-approval-triggers'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTrigger),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Create failed')
      setTriggers((prev) => [...prev, json.data])
      setShowAddForm(false)
      setNewTrigger({
        trigger_code: '',
        name: '',
        description: '',
        regulatory_reference: 'Internal policy',
        requirement_level: 'recommended',
        enabled: true,
      })
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(null)
    }
  }

  const deleteTrigger = async (triggerCode) => {
    if (!confirm('Are you sure you want to delete this trigger?')) return
    try {
      setSaving(triggerCode)
      const res = await authFetch(getLocalApiUrl(`/api/ecdd-approval-triggers/${triggerCode}`), {
        method: 'DELETE',
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Delete failed')
      setTriggers((prev) => prev.filter((t) => t.trigger_code !== triggerCode))
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(null)
    }
  }

  const generateCode = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .slice(0, 64)
  }

  const addApprover = async () => {
    if (!newApprover.approver_value) return
    try {
      setSaving('approver')
      const res = await authFetch(getLocalApiUrl('/api/ecdd-approval-triggers/approvers'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newApprover),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to add approver')
      setApprovers((prev) => [...prev, json.data])
      setShowAddApproverForm(false)
      setNewApprover({ approver_type: 'role', approver_value: '', display_name: '' })
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(null)
    }
  }

  const deleteApprover = async (id) => {
    if (!confirm('Remove this approver?')) return
    try {
      setSaving(`approver-${id}`)
      const res = await authFetch(getLocalApiUrl(`/api/ecdd-approval-triggers/approvers/${id}`), {
        method: 'DELETE',
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to remove approver')
      setApprovers((prev) => prev.filter((a) => a.id !== id))
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(null)
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="h-64 bg-gray-100 rounded"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <ShieldCheckIcon className="h-6 w-6 text-psg-teal" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">ECDD Approval Policies</h1>
              <p className="text-sm text-gray-500">
                Configure when ECDD decisions require senior management approval
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Add Custom Trigger
          </button>
        </div>
      </div>

      {/* How ECDD form fields map to these triggers */}
      <div className="card bg-slate-50 border-slate-200">
        <div className="flex gap-3">
          <InformationCircleIcon className="h-5 w-5 text-psg-teal flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700 space-y-1.5">
            <p className="font-semibold text-slate-900 m-0">How ECDD detail drives these policies</p>
            <p className="m-0 text-slate-600">
              When a CO submits an ECDD, PaySafeGo calls these triggers with fields from the structured form
              (screening PEP class, sanctions disposition, risk score) and the chosen decision
              (satisfactory / enhanced monitoring / escalate → SMR).
            </p>
            <ul className="m-0 pl-4 text-slate-600 list-disc space-y-0.5">
              <li><span className="font-medium text-slate-800">foreign_pep / domestic_pep</span> — Screening → true positive → PEP classification</li>
              <li><span className="font-medium text-slate-800">sanctions_match</span> — Screening hit dispositioned as true positive, not a PEP</li>
              <li><span className="font-medium text-slate-800">enhanced_monitoring / escalate_smr</span> — Decision section outcome</li>
              <li><span className="font-medium text-slate-800">pep_close_satisfactory / high_risk_score</span> — Decision + PEP / risk combination</li>
            </ul>
            <p className="m-0 text-xs text-slate-500">
              Approvers listed below gate who can approve on the ECDD detail page. Mandatory triggers always require approval; recommended triggers request approval; if nothing matches, ECDD may close directly.
            </p>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            ×
          </button>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="card bg-teal-50 border-teal-200">
          <h3 className="text-sm font-semibold text-psg-teal-900 mb-4">Add Custom Trigger</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g., High Value Close"
                value={newTrigger.name}
                onChange={(e) => {
                  setNewTrigger({
                    ...newTrigger,
                    name: e.target.value,
                    trigger_code: generateCode(e.target.value),
                  })
                }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Code</label>
              <input
                type="text"
                className="input-field bg-gray-50"
                value={newTrigger.trigger_code}
                onChange={(e) =>
                  setNewTrigger({ ...newTrigger, trigger_code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })
                }
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea
                className="input-field"
                rows={2}
                placeholder="Describe when this trigger should activate..."
                value={newTrigger.description}
                onChange={(e) => setNewTrigger({ ...newTrigger, description: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Policy Reference</label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g., Internal policy, Risk framework"
                value={newTrigger.regulatory_reference}
                onChange={(e) => setNewTrigger({ ...newTrigger, regulatory_reference: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Requirement Level</label>
              <select
                className="input-field"
                value={newTrigger.requirement_level}
                onChange={(e) => setNewTrigger({ ...newTrigger, requirement_level: e.target.value })}
              >
                <option value="mandatory">Mandatory - Blocks until approved</option>
                <option value="recommended">Recommended - Warning, can override</option>
                <option value="optional">Optional - Available but not enforced</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-teal-200">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={createTrigger}
              disabled={!newTrigger.trigger_code || !newTrigger.name || saving === 'new'}
              className="btn-primary disabled:opacity-50"
            >
              {saving === 'new' ? 'Creating...' : 'Create Trigger'}
            </button>
          </div>
        </div>
      )}

      {/* Triggers List */}
      <div className="card">
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Trigger
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Reference
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Level
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Enabled
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {triggers.map((trigger) => {
                const levelInfo = LEVEL_BADGES[trigger.requirement_level] || LEVEL_BADGES.optional
                const LevelIcon = LEVEL_ICONS[trigger.requirement_level] || CheckCircleIcon
                const isLocked = !trigger.can_be_disabled
                const isSaving = saving === trigger.trigger_code

                return (
                  <tr key={trigger.trigger_code} className={isSaving ? 'opacity-50' : ''}>
                    <td className="px-4 py-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-1.5 rounded-lg ${levelInfo.bg}`}>
                          <LevelIcon className={`h-4 w-4 ${levelInfo.text}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{trigger.name}</span>
                            {isLocked && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">
                                <LockClosedIcon className="h-3 w-3" />
                                Regulatory
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5 max-w-md">{trigger.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-600">{trigger.regulatory_reference || '—'}</span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="relative inline-block">
                        <select
                          value={trigger.requirement_level}
                          onChange={(e) => updateTrigger(trigger.trigger_code, { requirement_level: e.target.value })}
                          disabled={isSaving}
                          className={`appearance-none pl-3 pr-8 py-1.5 text-sm font-medium rounded-full border cursor-pointer
                            ${levelInfo.bg} ${levelInfo.text} ${levelInfo.border}`}
                        >
                          <option value="mandatory">Mandatory</option>
                          <option value="recommended">Recommended</option>
                          <option value="optional">Optional</option>
                        </select>
                        <ChevronDownIcon className={`absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 ${levelInfo.text} pointer-events-none`} />
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={trigger.enabled}
                          onChange={(e) => updateTrigger(trigger.trigger_code, { enabled: e.target.checked })}
                          disabled={isSaving || isLocked}
                          className="sr-only peer"
                        />
                        <div className={`w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer 
                          peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] 
                          after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 
                          after:border after:rounded-full after:h-4 after:w-4 after:transition-all 
                          peer-checked:bg-psg-teal peer-focus:ring-2 peer-focus:ring-psg-teal/30 ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                        ></div>
                      </label>
                    </td>
                    <td className="px-4 py-4 text-right">
                      {trigger.can_be_disabled && (
                        <button
                          onClick={() => deleteTrigger(trigger.trigger_code)}
                          disabled={isSaving}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete trigger"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ECDD Approvers Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-psg-teal" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">ECDD Approvers</h2>
              <p className="text-sm text-gray-500">
                Configure which roles or users can approve ECDD decisions
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowAddApproverForm(!showAddApproverForm)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Add Approver
          </button>
        </div>

        {/* Add Approver Form */}
        {showAddApproverForm && (
          <div className="mb-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
            <h3 className="text-sm font-semibold text-psg-teal-900 mb-3">Add New Approver</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                <select
                  className="input-field"
                  value={newApprover.approver_type}
                  onChange={(e) => setNewApprover({ ...newApprover, approver_type: e.target.value, approver_value: '', display_name: '' })}
                >
                  <option value="role">Role</option>
                  <option value="user">Specific User</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {newApprover.approver_type === 'role' ? 'Select Role' : 'Select User'}
                </label>
                {newApprover.approver_type === 'role' ? (
                  <select
                    className="input-field"
                    value={newApprover.approver_value}
                    onChange={(e) => {
                      const role = availableRoles.find(r => r.value === e.target.value)
                      setNewApprover({ 
                        ...newApprover, 
                        approver_value: e.target.value,
                        display_name: role?.label || e.target.value 
                      })
                    }}
                  >
                    <option value="">Select a role...</option>
                    {availableRoles
                      .filter(r => !approvers.some(a => a.approver_type === 'role' && a.approver_value === r.value))
                      .map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))
                    }
                  </select>
                ) : (
                  <select
                    className="input-field"
                    value={newApprover.approver_value}
                    onChange={(e) => {
                      const user = availableStaff.find(s => s.value === e.target.value)
                      setNewApprover({ 
                        ...newApprover, 
                        approver_value: e.target.value,
                        display_name: user?.label || e.target.value 
                      })
                    }}
                  >
                    <option value="">Select a user...</option>
                    {availableStaff
                      .filter(s => !approvers.some(a => a.approver_type === 'user' && a.approver_value === s.value))
                      .map(user => (
                        <option key={user.value} value={user.value}>
                          {user.label} ({user.email || user.value}){user.role ? ` - ${user.role}` : ''}
                        </option>
                      ))
                    }
                  </select>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Display Name</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Optional display name"
                  value={newApprover.display_name}
                  onChange={(e) => setNewApprover({ ...newApprover, display_name: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => { setShowAddApproverForm(false); setNewApprover({ approver_type: 'role', approver_value: '', display_name: '' }) }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={addApprover}
                disabled={!newApprover.approver_value || saving === 'approver'}
                className="btn-primary disabled:opacity-50"
              >
                {saving === 'approver' ? 'Adding...' : 'Add Approver'}
              </button>
            </div>
          </div>
        )}

        {/* Approvers List */}
        <div className="space-y-2">
          {approvers.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No approvers configured. Add roles or users who can approve ECDD decisions.
            </div>
          ) : (
            approvers.map((approver) => (
              <div
                key={approver.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  saving === `approver-${approver.id}` ? 'opacity-50' : ''
                } ${approver.approver_type === 'role' ? 'bg-teal-50/60 border-teal-200' : 'bg-teal-50 border-teal-200'}`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-teal-100">
                    {approver.approver_type === 'role' ? (
                      <UserGroupIcon className="h-4 w-4 text-psg-teal" />
                    ) : (
                      <UserIcon className="h-4 w-4 text-psg-teal" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{approver.display_name || approver.approver_value}</div>
                    <div className="text-xs text-gray-500">
                      {approver.approver_type === 'role' ? 'Role' : 'User'}: <span className="font-mono">{approver.approver_value}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => deleteApprover(approver.id)}
                  disabled={saving === `approver-${approver.id}`}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Remove approver"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Level Guide */}
      <div className="card bg-gray-50 border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Requirement Level Guide</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <div className="p-1.5 bg-red-100 rounded-lg">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">Mandatory</div>
              <p className="text-sm text-gray-500">
                Blocks ECDD submission until a senior manager approves in the Pending Approvals queue.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-1.5 bg-amber-100 rounded-lg">
              <InformationCircleIcon className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">Recommended</div>
              <p className="text-sm text-gray-500">
                Shows a warning and encourages approval, but allows override with a documented reason.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-1.5 bg-gray-100 rounded-lg">
              <CheckCircleIcon className="h-4 w-4 text-gray-500" />
            </div>
            <div>
              <div className="font-medium text-gray-900">Optional</div>
              <p className="text-sm text-gray-500">
                Available for awareness but not actively enforced. No warning or block.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Regulatory Note */}
      <div className="card bg-teal-50 border-teal-200">
        <div className="flex items-start gap-3">
          <LockClosedIcon className="h-5 w-5 text-psg-teal mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-psg-teal-900">Regulatory Triggers</h3>
            <p className="text-sm text-teal-800 mt-1">
              Triggers marked with <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded mx-1">
                <LockClosedIcon className="h-3 w-3" /> Regulatory
              </span> cannot be disabled as they are required by law (e.g., AUSTRAC Rules, Sanctions Act).
              You can adjust the requirement level but the trigger will remain active.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
